/**
 * PIKA-772 — when a change to the audit-log report's filters counts as a new search.
 *
 * The report builder's fetch effect re-runs for far more than a search: a rename mutates the
 * definition, a column or grouping change moves the fetch key, "try again" bumps the attempt
 * counter. Firing `Audit Log Report Searched` on every one of those would inflate the number the
 * event exists to measure, so each fetch is compared against the last one that was reported.
 *
 * Extracted from the effect because it is the part with a rule attached and the part that was
 * wrong once already. Keying on field names alone under-counted every value change — switching
 * Actor A to Actor B, or moving the date range, is a different search that reported nothing
 * (PIKA-772 QA, D2).
 *
 * The signature is only ever compared, never sent. It contains the filter VALUES, which are audit
 * content; the event carries field names and a result count.
 */

export type FilterCondition = {
	field?: string | null;
	operator?: string | null;
	values?: unknown;
};

/**
 * A stable string for one filter set: field, operator and values, in the order the builder holds
 * them. Reordering the same filters reads as a new search — accepted deliberately, because the
 * builder gives no way to reorder without editing, so the alternative is sorting on every fetch to
 * catch a case that does not arise.
 */
export const filterSignature = (conditions: ReadonlyArray<FilterCondition> | null | undefined): string =>
	JSON.stringify((conditions ?? []).map(condition => [condition.field, condition.operator, condition.values]));

/**
 * The filter FIELD names, which is all the event is allowed to carry. Nulls are dropped rather
 * than sent as `null`: a condition with no field is a half-built row in the filter editor, not a
 * dimension anyone can group by.
 */
export const filterFieldNames = (conditions: ReadonlyArray<FilterCondition> | null | undefined): string[] =>
	(conditions ?? [])
		.map(condition => condition.field)
		.filter((field): field is string => typeof field === 'string' && field.length > 0);

/** What to report for one fetch, or null when this fetch is not a new search. */
export type SearchReport = {signature: string; filterTypes: string[]};

/**
 * Whether this fetch is a new search, and what the event may carry if it is.
 *
 * Kept whole rather than split across the effect so the rule has one home and one test. The
 * caller keeps the last signature — it belongs to the component instance, and a module-level
 * cache would leak between two builders open at once.
 */
export const auditLogSearchReport = (
	conditions: ReadonlyArray<FilterCondition> | null | undefined,
	lastSignature: string | null
): SearchReport | null => {
	const signature = filterSignature(conditions);
	if (signature === lastSignature) {
		return null;
	}
	return {signature, filterTypes: filterFieldNames(conditions)};
};
